import React, { useState } from 'react';
import { CheckCircle, Clock, XCircle, CheckCircle2, Plus } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { $api } from '../../api';
import { useOrganization } from '../../contexts/OrganizationContext';
import AlertComparison from '../Alerts/AlertComparison';
import ErrorMessage from '../ErrorMessage';
import { Client } from '../../types';
import SuccessMessage from '../SuccessMessage';
import type { paths } from '../../api/paths';

// Extract alert type from API definitions
type AlertFromAPI = paths['/api/organization/{organization_id}/alerts']['get']['responses']['200']['content']['application/json']['alerts'][0];

const EntityAlerts: React.FC = () => {
  const { clientId, organizationId } = useParams<{ clientId: string; organizationId: string }>();
  const { currentOrganization } = useOrganization();
  const orgId = organizationId || currentOrganization?.id || '';
  const entityId = clientId || '';
  
  const [selectedAlert, setSelectedAlert] = useState<AlertFromAPI | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Mutation for creating alerts
  const createAlertsMutation = $api.useMutation('post', '/api/organization/{organization_id}/alerts');

  // Fetch entity details
  const entityQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}',
    {
      params: {
        path: {
          organization_id: orgId,
          id: entityId,
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  // Fetch alerts for this entity
  const alertsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/alerts',
    {
      params: {
        path: {
          organization_id: orgId,
        },
        query: {
          entity_id: entityId,
        },
      },
    },
    {
      enabled: !!orgId && !!entityId,
    }
  );

  const alerts = alertsQuery.data?.alerts || [];
  const entityData = entityQuery.data;
  
  // Create Client object from entity data
  const entity: Client | null = entityData ? {
    id: entityData.id,
    name: entityData.name,
    email: entityData.email || '',
    phone: entityData.phone || '',
    company: entityData.kind === 'company' ? entityData.name : '',
    status: 'verified',
    kycStatus: 'completed',
    createdAt: entityData.created_at,
    lastUpdated: entityData.updated_at,
    riskScore: entityData.risk_level === 'high' ? 80 : entityData.risk_level === 'mid' ? 50 : 20,
    documents: [],
  } : null;

  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction':
        return 'bg-red-100 text-red-800';
      case 'pep':
        return 'bg-orange-100 text-orange-800';
      case 'adverse_media':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'sanctioned':
      case 'sanction':
        return 'Sanction';
      case 'pep':
        return 'PEP';
      case 'adverse_media':
        return 'Média défavorable';
      default:
        return type;
    }
  };

  const getStatusBadge = (resolutionType: 'false_positive' | 'match' | undefined) => {
    if (!resolutionType) {
      return {
        label: 'En attente',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: Clock,
      };
    }
    
    if (resolutionType === 'match') {
      return {
        label: 'Correspondance confirmée',
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: XCircle,
      };
    }
    
    return {
      label: 'Fausse alerte',
      color: 'bg-green-100 text-green-800 border-green-300',
      icon: CheckCircle2,
    };
  };

  const getSeverityColor = (severity: 'low' | 'mid' | 'high') => {
    switch (severity) {
      case 'high':
        return 'text-red-600';
      case 'mid':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleAlertClick = (alert: AlertFromAPI) => {
    setSelectedAlert(alert);
    setShowComparison(true);
  };

  const handleResolveAlert = (alertId: string, isMatch: boolean, reason?: string) => {
    // Handle alert resolution
    console.log('Resolving alert:', { alertId, isMatch, reason });
    setShowComparison(false);
    setSelectedAlert(null);
    // Refetch alerts and entity to update the list and alerts_pending count
    alertsQuery.refetch();
    entityQuery.refetch();
  };

  const handleCreateMockAlerts = async () => {
    if (!entityData || !orgId || !entityId) return;

    const isPerson = entityData.kind === 'person';
    const entityName = entityData.name;
    const firstName = entityData.first_name || entityName.split(' ')[0];
    const lastName = entityData.last_name || entityName.split(' ').slice(1).join(' ') || entityName;

    // Generate mock alerts based on entity type
    const mockAlerts = [
      {
        entity_id: entityId,
        detected_info: {
          name: isPerson ? `${firstName.toUpperCase()} ${lastName.toUpperCase()}` : entityName.toUpperCase(),
          first_name: isPerson ? firstName : undefined,
          last_name: isPerson ? lastName : undefined,
          birth_date: isPerson ? entityData.birth_date : undefined,
          nationality: isPerson ? entityData.country : undefined,
          list_name: 'Liste de sanctions OFAC',
          description: 'Correspondance potentielle trouvée dans les listes de sanctions internationales',
          sanctioned_date: '2023-08-15',
          alert_type: 'sanctioned' as const,
        },
      },
      {
        entity_id: entityId,
        detected_info: {
          name: isPerson ? `${firstName.toUpperCase()} ${lastName.toUpperCase()}` : entityName.toUpperCase(),
          first_name: isPerson ? firstName : undefined,
          last_name: isPerson ? lastName : undefined,
          list_name: 'Liste PEP Mondiale',
          description: 'Personne politiquement exposée détectée',
          alert_type: 'pep' as const,
        },
      },
    ];

    try {
      setErrorMessage(null);
      await createAlertsMutation.mutateAsync({
        params: {
          path: {
            organization_id: orgId,
          },
        },
        body: {
          alerts: mockAlerts,
        },
      });

      setSuccessMessage('Alertes de test créées avec succès');
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // Refetch alerts to show the new ones
      alertsQuery.refetch();
    } catch (error: unknown) {
      console.error('Error creating mock alerts:', error);
      const errorMsg = error && typeof error === 'object' && 'error' in error
        ? String(error.error)
        : 'Erreur lors de la création des alertes de test';
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  if (entityQuery.isLoading || alertsQuery.isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des alertes...</span>
        </div>
      </div>
    );
  }

  if (entityQuery.error || alertsQuery.error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <ErrorMessage error={entityQuery.error || alertsQuery.error} />
      </div>
    );
  }

  const entityName = entityData?.name || (alerts.length > 0 ? alerts[0].entity_name : 'cette entité');

  const isOwner = currentOrganization?.role === 'owner';

  return (
    <>
      {successMessage && (
        <div className="mb-4">
          <SuccessMessage message={successMessage} />
        </div>
      )}
      {errorMessage && (
        <div className="mb-4">
          <ErrorMessage error={errorMessage} />
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Alertes pour {entityName}</h3>
          {isOwner && alerts.length === 0 && (
            <button
              onClick={handleCreateMockAlerts}
              disabled={createAlertsMutation.isPending || !entityData}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Créer des alertes de test</span>
            </button>
          )}
        </div>
        
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Aucune alerte</h4>
            <p className="text-gray-500">Cette entité ne présente aucune alerte.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => {
              const statusBadge = getStatusBadge(alert.resolution_type);
              const StatusIcon = statusBadge.icon;
              
              return (
                <div key={alert.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAlertTypeColor(alert.alert_type)}`}>
                        {getAlertTypeLabel(alert.alert_type)}
                      </span>
                      <span className={`px-2 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full border ${statusBadge.color}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusBadge.label}
                      </span>
                      <span className={`text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                        {alert.severity === 'high' ? 'Élevé' : alert.severity === 'mid' ? 'Moyen' : 'Faible'}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(alert.date || alert.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 mb-2">{alert.description}</p>
                    {alert.matching_percentage && (
                      <p className="text-xs text-gray-500">
                        Correspondance: {alert.matching_percentage}%
                      </p>
                    )}
                    {alert.list_name && (
                      <p className="text-xs text-gray-500">
                        Liste: {alert.list_name}
                      </p>
                    )}
                    {alert.resolution_comment && (
                      <p className="text-xs text-gray-600 mt-2 italic">
                        Commentaire: {alert.resolution_comment}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    {alert.resolution_type && alert.resolution_date && (
                      <span className="text-xs text-gray-500">
                        Résolue le {new Date(alert.resolution_date).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                    <button 
                      onClick={() => handleAlertClick(alert)}
                      className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Analyser
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alert Comparison Modal */}
      {showComparison && selectedAlert && entity && (
        <AlertComparison
          alert={selectedAlert}
          entity={entity}
          onClose={() => {
            setShowComparison(false);
            setSelectedAlert(null);
          }}
          onResolve={handleResolveAlert}
        />
      )}
    </>
  );
};

export default EntityAlerts;

